import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { MapPin, NavigationArrow, MapPinLine, SignOut, Play, CheckCircle, Broadcast, Package } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DriverView() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useDocumentTitle("My Trips");
  const [trips, setTrips] = useState([]);
  const [active, setActive] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [lastPing, setLastPing] = useState(null);
  const watchRef = useRef(null);

  const load = () => api.get("/shipments").then((r) => setTrips(r.data));
  useEffect(() => { load(); return () => stopTracking(); }, []); // eslint-disable-line

  const stopTracking = () => {
    if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; }
    setTracking(false);
  };

  const startTrip = async (trip) => {
    setActive(trip);
    if (trip.status === "created") {
      await api.post(`/shipments/${trip.id}/status`, { status: "dispatched" });
    }
    startTracking(trip);
  };

  const sendPing = async (tripId, lat, lng) => {
    try { await api.post(`/shipments/${tripId}/gps`, { lat, lng }); setLastPing({ lat, lng, ts: new Date().toISOString() }); }
    catch { /* ignore intermittent */ }
  };

  const startTracking = (trip) => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported on this device");
    setTracking(true);
    toast.success("Trip started — reporting GPS");
    navigator.geolocation.getCurrentPosition(
      (p) => sendPing(trip.id, p.coords.latitude, p.coords.longitude),
      () => { sendPing(trip.id, trip.origin.lat, trip.origin.lng); toast.message("Using approx origin (location blocked)"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => sendPing(trip.id, p.coords.latitude, p.coords.longitude),
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000 }
    );
  };

  const tapCheckpoint = async () => {
    if (!active) return;
    const doTap = (lat, lng) => api.post(`/shipments/${active.id}/checkpoint`, { label: `Checkpoint — ${new Date().toLocaleTimeString()}`, lat, lng })
      .then(() => { toast.success("Checkpoint logged"); load(); });
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => doTap(p.coords.latitude, p.coords.longitude),
        () => doTap(active.origin.lat, active.origin.lng), { timeout: 6000 }
      );
    } else doTap(active.origin.lat, active.origin.lng);
  };

  // Active trip screen
  if (active) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex flex-col">
        <div className="p-5 flex items-center justify-between border-b border-white/10">
          <button data-testid="driver-back" onClick={() => { stopTracking(); setActive(null); load(); }} className="text-sm text-slate-300">← Trips</button>
          <StatusBadge status={active.status} />
        </div>
        <div className="flex-1 p-5 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-400">Active Trip</div>
            <div className="font-mono text-2xl font-semibold mt-1">{active.code}</div>
            <div className="text-slate-300 mt-1 flex items-center gap-2 text-lg">{active.origin.name} <NavigationArrow size={18} /> {active.destination.name}</div>
          </div>

          <div className="rounded-lg bg-white/5 border border-white/10 p-5">
            <div className="flex items-center gap-2 text-sm">
              <span className={`h-3 w-3 rounded-full ${tracking ? "bg-green-400 live-dot" : "bg-slate-500"}`} />
              {tracking ? "GPS reporting active" : "GPS paused"}
            </div>
            {lastPing && <div className="text-xs text-slate-400 mt-2 font-mono">Last ping: {lastPing.lat.toFixed(4)}, {lastPing.lng.toFixed(4)} · {new Date(lastPing.ts).toLocaleTimeString()}</div>}
            <div className="text-xs text-slate-500 mt-2">GPS updates pause when the phone screen is off — that's expected.</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-white/5 border border-white/10 p-4"><div className="text-xs text-slate-400 uppercase tracking-wider">Distance</div><div className="font-mono text-2xl mt-1">{active.distance_km}<span className="text-sm text-slate-400"> km</span></div></div>
            <div className="rounded-lg bg-white/5 border border-white/10 p-4"><div className="text-xs text-slate-400 uppercase tracking-wider">ETA</div><div className="font-mono text-2xl mt-1">{active.eta_hours}<span className="text-sm text-slate-400"> h</span></div></div>
          </div>
        </div>

        <div className="p-5 space-y-3 border-t border-white/10">
          <Button data-testid="tap-checkpoint" onClick={tapCheckpoint} className="w-full h-14 text-base bg-blue-600 hover:bg-blue-700">
            <MapPinLine size={22} className="mr-2" /> Tap Checkpoint
          </Button>
          {tracking
            ? <Button data-testid="pause-gps" onClick={stopTracking} variant="outline" className="w-full h-12 bg-transparent border-white/20 text-white hover:bg-white/10">Pause GPS</Button>
            : <Button data-testid="resume-gps" onClick={() => startTracking(active)} className="w-full h-12 bg-green-600 hover:bg-green-700"><Broadcast size={18} className="mr-2" />Resume GPS</Button>}
        </div>
      </div>
    );
  }

  // Trip list
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="bg-slate-900 text-white px-5 pt-6 pb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-400">Driver</div>
            <div className="font-heading text-2xl font-bold">{user.name}</div>
          </div>
          <button data-testid="driver-logout" onClick={() => { logout(); navigate("/login"); }} className="h-10 w-10 rounded-md bg-white/10 flex items-center justify-center"><SignOut size={18} /></button>
        </div>
      </div>
      <div className="p-5 -mt-4 space-y-3">
        <div className="text-xs uppercase tracking-wider font-bold text-slate-500 px-1">My Trips ({trips.length})</div>
        {trips.length === 0 && <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-slate-400">No trips assigned.</div>}
        {trips.map((t) => (
          <div key={t.id} data-testid={`trip-${t.code}`} className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono font-semibold text-slate-900">{t.code}</span>
              <StatusBadge status={t.status} />
            </div>
            <div className="flex items-center gap-2 text-slate-600 mb-3"><MapPin size={16} className="text-slate-400" />{t.origin.name} → {t.destination.name}</div>
            <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
              <span><Package size={13} className="inline mr-1" />{t.quantity_tons} t</span>
              <span>{t.distance_km} km</span>
              <span>ETA {t.eta_hours} h</span>
            </div>
            {t.status !== "delivered"
              ? <Button data-testid={`start-trip-${t.code}`} onClick={() => startTrip(t)} className="w-full h-11 bg-slate-900 hover:bg-slate-800"><Play size={16} weight="fill" className="mr-2" />{t.status === "created" ? "Start Trip" : "Open Trip"}</Button>
              : <div className="flex items-center justify-center gap-2 text-green-600 text-sm py-2"><CheckCircle size={18} weight="fill" />Delivered</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
